import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, shortINR, LOAN_TYPE_LABEL } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Link } from "react-router-dom";
import {
  UserPlus, Users, TrendingUp, CalendarClock, Shield, FileText, IndianRupee, RefreshCcw,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

const STATUS_COLORS = {
  new: "#94A3B8", documents_pending: "#F59E0B", under_review: "#0052FF",
  approved: "#059669", disbursed: "#10B981", rejected: "#DC2626", closed: "#52525B",
};

function Kpi({ icon: Icon, label, value, hint, color = "#0052FF", testid }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}12`, color }}>
          <Icon size={18} />
        </div>
      </div>
      <div className="mt-4 font-display text-3xl font-bold text-[#06152D] font-mono-num">{value}</div>
      <div className="mt-1 text-xs font-semibold tracking-[0.1em] uppercase text-[#52525B]">{label}</div>
      {hint && <div className="mt-1 text-xs text-[#52525B]">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, refetch, isFetching } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 60000,
  });

  const k = data?.kpis || {};
  const pipeline = data?.pipeline || [];
  const byType = (data?.by_type || []).map((r) => ({
    ...r, label: LOAN_TYPE_LABEL[r.loan_type] || r.loan_type,
  }));

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-[#52525B] mt-1">Role: {ROLE_LABEL[user?.role]} · {user?.branch || "Siddipet"}</p>
        </div>
        <button onClick={() => refetch()} className="text-sm text-[#0052FF] hover:text-[#003FCC] inline-flex items-center gap-1" data-testid="dashboard-refresh">
          <RefreshCcw size={14} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={UserPlus} label="Today's Leads" value={k.today_leads ?? 0} hint={`${k.total_leads ?? 0} total`} testid="kpi-today-leads" />
        <Kpi icon={CalendarClock} label="Today's Follow-ups" value={k.today_followups ?? 0} color="#0052FF" testid="kpi-today-followups" />
        <Kpi icon={CalendarClock} label="Overdue Follow-ups" value={k.overdue_followups ?? 0} color="#DC2626" testid="kpi-overdue-followups" />
        <Kpi icon={CalendarClock} label="Upcoming (7d)" value={k.upcoming_followups ?? 0} color="#F59E0B" testid="kpi-upcoming-followups" />
        <Kpi icon={UserPlus} label="New Leads" value={k.new_leads ?? 0} hint={`${k.conversion_rate ?? 0}% conversion`} color="#F59E0B" testid="kpi-new-leads" />
        <Kpi icon={Users} label="Customers" value={k.total_customers ?? 0} hint={`${k.loan_customers ?? 0} loan · ${k.insurance_customers ?? 0} ins.`} color="#059669" testid="kpi-customers" />
        <Kpi icon={FileText} label="Loan Files" value={k.total_loans ?? 0} hint={`${k.approved_loans ?? 0} approved · ${k.disbursed_loans ?? 0} disbursed`} testid="kpi-loans" />
        <Kpi icon={IndianRupee} label="Disbursed Value" value={shortINR(k.total_disbursed_amount)} color="#10B981" testid="kpi-disbursed" />
        <Kpi icon={Shield} label="Insurance Policies" value={k.total_insurance ?? 0} color="#7C3AED" testid="kpi-insurance" />
        <Kpi icon={TrendingUp} label="Renewals in 30 days" value={k.renewals_due ?? 0} hint="Contact for renewal" color="#EA580C" testid="kpi-renewals" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E4E4E7] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-[#06152D]">Loan pipeline (files by status)</h3>
              <p className="text-xs text-[#52525B]">Count of active loan files by current status</p>
            </div>
          </div>
          <div className="mt-4 h-64 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipeline} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="status" tickFormatter={(s) => s?.replace(/_/g, " ")} />
                <YAxis allowDecimals={false} />
                <Tooltip cursor={{ fill: "#EBF2FF" }} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {pipeline.map((p) => (
                    <Cell key={p.status} fill={STATUS_COLORS[p.status] || "#0052FF"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-xl p-5">
          <h3 className="font-display font-bold text-[#06152D]">Recent leads</h3>
          <ul className="mt-4 space-y-3">
            {(data?.recent_leads || []).length === 0 && <li className="text-sm text-[#52525B]">No leads yet.</li>}
            {(data?.recent_leads || []).map((l) => (
              <li key={l.id} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-[#EBF2FF] text-[#0052FF] flex items-center justify-center text-xs font-semibold shrink-0">
                  {(l.full_name || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#06152D] truncate">{l.full_name}</div>
                  <div className="text-xs text-[#52525B]">{LOAN_TYPE_LABEL[l.loan_type]} · {l.phone}</div>
                </div>
                <span className="text-xs text-[#52525B]">{new Date(l.created_at).toLocaleDateString("en-IN")}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin/leads" className="mt-4 block text-center text-sm font-semibold text-[#0052FF] hover:text-[#003FCC]" data-testid="dashboard-view-leads">
            View all leads →
          </Link>
        </div>
      </div>

      {byType.length > 0 && (
        <div className="mt-6 bg-white border border-[#E4E4E7] rounded-xl p-5">
          <h3 className="font-display font-bold text-[#06152D]">Loans by product</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byType} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="label" angle={-15} textAnchor="end" height={70} interval={0} />
                <YAxis allowDecimals={false} />
                <Tooltip cursor={{ fill: "#EBF2FF" }} />
                <Bar dataKey="count" fill="#0052FF" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
